import { Controller } from "stimulus"

// Code a usage unique (OTP) saisi dans une rangee de cases.
//
//   <div data-controller="otp" data-otp-autosubmit-value="true">
//     <input type="hidden" name="confirmation_code" data-otp-target="value">
//     <input inputmode="numeric" data-otp-target="digit" data-action="input->otp#input keydown->otp#keydown paste->otp#paste focus->otp#select click->otp#select">
//     ... une case par chiffre
//   </div>
//
// - une seule valeur est postee : le champ cache, mis a jour a chaque frappe ;
// - saisie : avance a la case suivante ; Retour arriere recule ; fleches, Home, End ;
// - collage (ou remplissage automatique du SMS) : le code est reparti sur les cases ;
// - autosubmit : envoie le formulaire des que toutes les cases sont remplies.
export default class extends Controller {
  static targets = ["digit", "value"]
  static values = { autosubmit: Boolean }

  connect() {
    this.submitted = false
    this.sync()
  }

  // ── Actions ────────────────────────────────────────────────────────────────
  input(event) {
    const field = event.target
    const index = this.digitTargets.indexOf(field)
    const digits = field.value.replace(/\D/g, "")

    if (digits.length >= this.size) {
      // Code complet d'un coup (autofill SMS) : on le repartit depuis la 1re case.
      this.fill(digits, 0)
    } else if (digits.length === 0) {
      field.value = ""
      this.sync()
    } else {
      // Case deja remplie + nouvelle frappe : on garde le dernier chiffre tape.
      field.value = digits.slice(-1)
      this.sync()
      this.focusAt(index + 1)
      this.submitIfComplete()
    }
  }

  keydown(event) {
    const index = this.digitTargets.indexOf(event.target)

    switch (event.key) {
      case "Backspace":
        // Case vide : on efface la precedente et on recule.
        if (!event.target.value && index > 0) {
          event.preventDefault()
          this.digitTargets[index - 1].value = ""
          this.sync()
          this.focusAt(index - 1)
        }
        break
      case "ArrowLeft":
        event.preventDefault()
        this.focusAt(index - 1)
        break
      case "ArrowRight":
        event.preventDefault()
        this.focusAt(index + 1)
        break
      case "Home":
        event.preventDefault()
        this.focusAt(0)
        break
      case "End":
        event.preventDefault()
        this.focusAt(this.size - 1)
        break
    }
  }

  paste(event) {
    const clipboard = event.clipboardData || window.clipboardData
    if (!clipboard) return

    const digits = clipboard.getData("text").replace(/\D/g, "")
    event.preventDefault()
    if (!digits) return

    // Un code complet colle dans n'importe quelle case remplit toute la rangee.
    const start = digits.length >= this.size ? 0 : this.digitTargets.indexOf(event.target)
    this.fill(digits, start)
  }

  select(event) {
    event.target.select()
  }

  // ── Interne ────────────────────────────────────────────────────────────────
  get size() {
    return this.digitTargets.length
  }

  fill(digits, start) {
    const cleanDigits = this.normalizeCode(digits)
    if (!cleanDigits) return

    this.digitTargets.forEach((digit) => {
      digit.value = ""
    })

    let written = 0

    for (let position = start; position < this.size && written < cleanDigits.length; position++) {
      this.digitTargets[position].value = cleanDigits[written]
      written++
    }

    this.sync()
    this.focusAt(Math.min(start + written, this.size - 1))
    this.submitIfComplete()
  }

  focusAt(index) {
    const target = this.digitTargets[Math.max(0, Math.min(index, this.size - 1))]
    if (!target) return

    target.focus()
    target.select()
  }

  sync() {
    const rawCode = this.digitTargets.map((digit) => digit.value).join("")
    const code = this.normalizeCode(rawCode)

    if (this.hasValueTarget) this.valueTarget.value = code
    this.digitTargets.forEach((digit) => digit.classList.toggle("is-filled", digit.value !== ""))
  }

  normalizeCode(code) {
    const digits = code.replace(/\D/g, "")
    if (digits.length <= this.size) return digits

    const deduplicated = digits.slice(0, this.size)
    if (digits.slice(this.size) === deduplicated) return deduplicated

    return digits.slice(0, this.size)
  }

  submitIfComplete() {
    if (!this.autosubmitValue || this.submitted) return
    if (this.hasValueTarget && this.valueTarget.value.length < this.size) return

    const form = this.element.closest("form")
    if (!form) return

    this.submitted = true
    if (form.requestSubmit) form.requestSubmit()
    else form.submit()
  }
}
